import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authAPI } from '../services/api';
import { normalizeRole, normalizeUserRole } from '../utils/roleNormalization';

const TRUSTED_2FA_STORAGE_KEY = 'trusted_2fa_device_tokens';

const readTrustedMap = () => {
  try {
    const raw = localStorage.getItem(TRUSTED_2FA_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const getTrustedTokenForEmail = (email) => {
  if (!email) return '';
  const map = readTrustedMap();
  return String(map[String(email).toLowerCase()] || '');
};

const setTrustedTokenForEmail = (email, token) => {
  if (!email) return;
  const key = String(email).toLowerCase();
  const map = readTrustedMap();
  if (token) map[key] = token;
  else delete map[key];
  localStorage.setItem(TRUSTED_2FA_STORAGE_KEY, JSON.stringify(map));
};

const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      twoFactorPending: null,

      // Login
      login: async (email, password) => {
        set({ isLoading: true, error: null });
        try {
          const normalizedEmail = String(email || '').toLowerCase().trim();
          const trustedDeviceToken = getTrustedTokenForEmail(normalizedEmail);
          const { data } = await authAPI.login({ email, password, trustedDeviceToken });
          if (data?.requiresTwoFactor) {
            set({
              twoFactorPending: {
                email: data.email || normalizedEmail,
                challengeId: data.challengeId,
                method: data.method || 'email',
                maskedEmail: data.maskedEmail || '',
                devCode: data.devCode || '',
                issuedAt: Date.now(),
              },
              isAuthenticated: false,
              isLoading: false,
            });
            return data;
          }
          localStorage.setItem('token', data.token);
          set({
            user: normalizeUserRole(data.user),
            token: data.token,
            isAuthenticated: true,
            twoFactorPending: null,
            isLoading: false,
          });
          return data;
        } catch (error) {
          set({ error: error.message, isLoading: false });
          throw error;
        }
      },

      loginWithPasskey: async (email, credential) => {
        set({ isLoading: true, error: null });
        try {
          const normalizedEmail = String(email || '').toLowerCase().trim();
          const verifyRes = await authAPI.passkeyLoginVerify({
            email: normalizedEmail,
            credential,
          });
          const data = verifyRes.data;
          localStorage.setItem('token', data.token);
          set({
            user: normalizeUserRole(data.user),
            token: data.token,
            isAuthenticated: true,
            twoFactorPending: null,
            isLoading: false,
          });
          return data;
        } catch (error) {
          set({ error: error.message, isLoading: false });
          throw error;
        }
      },

      completeTwoFactorLogin: async (email, challengeId, code, rememberDevice = false) => {
        set({ isLoading: true, error: null });
        try {
          const { data } = await authAPI.login2fa({ email, challengeId, code, rememberDevice });
          if (data?.trustedDeviceToken) {
            setTrustedTokenForEmail(email, data.trustedDeviceToken);
          }
          localStorage.setItem('token', data.token);
          set({
            user: normalizeUserRole(data.user),
            token: data.token,
            isAuthenticated: true,
            twoFactorPending: null,
            isLoading: false,
          });
          return data;
        } catch (error) {
          set({ error: error.message, isLoading: false });
          throw error;
        }
      },

      clearTwoFactorPending: () => set({ twoFactorPending: null }),

      // Register
      register: async (userData) => {
        set({ isLoading: true, error: null });
        try {
          const { data } = await authAPI.register(userData);
          localStorage.setItem('token', data.token);
          set({
            user: normalizeUserRole(data.user),
            token: data.token,
            isAuthenticated: true,
            isLoading: false,
          });
          return data;
        } catch (error) {
          set({ error: error.message, isLoading: false });
          throw error;
        }
      },

      // Logout
      logout: () => {
        localStorage.removeItem('token');
        localStorage.removeItem('auth-storage');
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          twoFactorPending: null,
          error: null,
        });
      },

      // Fetch current user
      fetchUser: async () => {
        const token = localStorage.getItem('token');
        if (!token) {
          set({ isAuthenticated: false });
          return null;
        }

        set({ isLoading: true });
        try {
          const { data } = await authAPI.getMe();
          set({
            user: normalizeUserRole(data.user),
            isAuthenticated: true,
            isLoading: false,
          });
          return normalizeUserRole(data.user);
        } catch (error) {
          localStorage.removeItem('token');
          localStorage.removeItem('auth-storage');
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
          });
          return null;
        }
      },

      // Update profile
      updateProfile: async (profileData) => {
        set({ isLoading: true, error: null });
        try {
          const { data } = await authAPI.updateProfile(profileData);
          set({
            user: normalizeUserRole(data.user),
            isLoading: false,
          });
          return normalizeUserRole(data.user);
        } catch (error) {
          set({ error: error.message, isLoading: false });
          throw error;
        }
      },

      // Check role
      hasRole: (roles) => {
        const user = get().user;
        if (!user) return false;
        const userRole = normalizeRole(user.role);
        const normalizedRoles = Array.isArray(roles) ? roles : [roles];
        if (userRole === 'stratel_admin' || userRole === 'admin') {
          return normalizedRoles.includes('admin') || normalizedRoles.includes('stratel_admin');
        }
        return normalizedRoles.includes(userRole);
      },

      // Check if internal team
      isInternalTeam: () => {
        return get().hasRole(['admin', 'stratel_admin', 'consultant', 'consultant_lead']);
      },

      // Check if admin
      isAdmin: () => {
        return get().hasRole(['admin', 'stratel_admin']);
      },

      // Clear error
      clearError: () => set({ error: null }),
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
      merge: (persistedState, currentState) => {
        const typed = persistedState && typeof persistedState === 'object' ? persistedState : {};
        return {
          ...currentState,
          ...typed,
          user: normalizeUserRole(typed.user || currentState.user),
        };
      },
    }
  )
);

export default useAuthStore;
